/**
 * Always-visible save/publish status for the builder bar: one slot that tells
 * the user which state the page is in instead of staying silent between
 * transient events. Pure — the bar feeds it snapshots, tests feed it rows.
 */

export type BarStatusTone = 'muted' | 'warning' | 'danger' | 'success';

export interface BarStatus {
    /** builder_v2 lang key. */
    key: string;
    tone: BarStatusTone;
}

export interface BarStatusSnapshot {
    status: 'loading' | 'idle' | 'saving' | 'saved' | 'error' | 'readonly';
    /** Working copy differs from the published content. */
    changed: boolean;
    publishing: boolean;
    /** Transient post-publish success flash. */
    published: boolean;
}

/** First matching row wins; null = empty slot (Lite without local edits). */
export function barSaveStatus(autosave: BarStatusSnapshot | null, dirty: boolean): BarStatus | null {
    if (!autosave) {
        return dirty ? { key: 'lite_unsaved_hint', tone: 'muted' } : null;
    }

    if (autosave.status === 'loading') {
        return { key: 'loading_draft', tone: 'muted' };
    }

    if (autosave.status === 'readonly') {
        return { key: 'readonly_status', tone: 'warning' };
    }

    if (autosave.publishing) {
        return { key: 'publishing', tone: 'muted' };
    }

    if (autosave.published) {
        return { key: 'published_status', tone: 'success' };
    }

    if (autosave.status === 'saving') {
        return { key: 'saving', tone: 'muted' };
    }

    if (autosave.status === 'error') {
        return { key: 'save_error', tone: 'danger' };
    }

    // Local mutations with the debounce still pending read as "saving" — the
    // user cares that their edit is on its way, not about the debounce window.
    if (dirty) {
        return { key: 'saving', tone: 'muted' };
    }

    if (autosave.changed) {
        return { key: 'saved_unpublished', tone: 'muted' };
    }

    return { key: 'published', tone: 'muted' };
}
